// WhatsApp Business account credentials: the Meta app a message is sent with.
// Route: /settings/accounts

import { useState } from "react";

import { http } from "../api";
import { useSession } from "../App";
import {
  Badge,
  Banner,
  Confirm,
  CopyButton,
  Dialog,
  DialogFooter,
  Empty,
  Field,
  SkeletonRows,
  Spinner,
  Tag,
  Toggle,
  useAsync,
  useToast,
  type BadgeTone,
} from "../ui";
import { BASE_PATH, errMessage, ErrorBanner } from "./shared";
import { BusinessProfileDialog } from "./business_profile";

// ============================================================ accounts

export interface WhatsAppAccount {
  id: string;
  name: string;
  app_id: string;
  phone_id: string;
  business_id: string;
  webhook_verify_token: string;
  api_version: string;
  is_default_incoming: boolean;
  is_default_outgoing: boolean;
  auto_read_receipt: boolean;
  status: string;
  has_access_token: boolean;
  has_app_secret: boolean;
  phone_number?: string;
  display_name?: string;
  created_at: string;
  updated_at: string;
}

interface AccountsPayload {
  accounts?: WhatsAppAccount[];
}

interface AccountTestResult {
  success: boolean;
  error?: string;
  display_phone_number?: string;
  verified_name?: string;
  quality_rating?: string;
  messaging_limit_tier?: string;
  code_verification_status?: string;
  account_mode?: string;
  is_test_number?: boolean;
  warning?: string;
}

interface AccountSubscribeResult {
  success: boolean;
  message?: string;
  error?: string;
}

interface AccountForm {
  name: string;
  app_id: string;
  phone_id: string;
  business_id: string;
  access_token: string;
  app_secret: string;
  webhook_verify_token: string;
  api_version: string;
  is_default_incoming: boolean;
  is_default_outgoing: boolean;
  auto_read_receipt: boolean;
}

const EMPTY_ACCOUNT_FORM: AccountForm = {
  name: "",
  app_id: "",
  phone_id: "",
  business_id: "",
  access_token: "",
  app_secret: "",
  webhook_verify_token: "",
  api_version: "v21.0",
  is_default_incoming: false,
  is_default_outgoing: false,
  auto_read_receipt: false,
};

const ACCOUNT_STATUS_TONE: Record<string, BadgeTone> = {
  active: "good",
  inactive: "neutral",
  error: "critical",
};

export function AccountsView() {
  const { can } = useSession();
  const toast = useToast();
  const canWrite = can("accounts", "write");
  const canDelete = can("accounts", "delete");

  const { data, error, loading, reload } = useAsync<AccountsPayload>(
    () => http.get<AccountsPayload>("/accounts"),
    []
  );
  const accounts = data?.accounts ?? [];

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<WhatsAppAccount | null>(null);
  const [form, setForm] = useState<AccountForm>(EMPTY_ACCOUNT_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [toDelete, setToDelete] = useState<WhatsAppAccount | null>(null);
  const [testing, setTesting] = useState<string | null>(null);
  const [subscribing, setSubscribing] = useState<string | null>(null);
  const [tests, setTests] = useState<Record<string, AccountTestResult>>({});
  const [profileFor, setProfileFor] = useState<WhatsAppAccount | null>(null);

  const webhookUrl = `${window.location.origin}${BASE_PATH}/api/webhook`;

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_ACCOUNT_FORM);
    setDialogOpen(true);
  };

  const openEdit = (account: WhatsAppAccount) => {
    setEditing(account);
    setForm({
      name: account.name,
      app_id: account.app_id || "",
      phone_id: account.phone_id,
      business_id: account.business_id,
      // Secrets are never pre-filled; blank means "keep the stored one".
      access_token: "",
      app_secret: "",
      webhook_verify_token: account.webhook_verify_token,
      api_version: account.api_version,
      is_default_incoming: account.is_default_incoming,
      is_default_outgoing: account.is_default_outgoing,
      auto_read_receipt: account.auto_read_receipt,
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.name.trim() || !form.phone_id.trim() || !form.business_id.trim()) {
      toast.push("Name, phone ID and business ID are required", "critical");
      return;
    }
    if (!editing && !form.access_token.trim()) {
      toast.push("An access token is required to connect a new account", "critical");
      return;
    }

    setSubmitting(true);
    try {
      const payload: Record<string, unknown> = { ...form };
      // Sending a blank secret on an edit would overwrite the stored one.
      if (editing && !form.access_token) delete payload.access_token;
      if (editing && !form.app_secret) delete payload.app_secret;

      if (editing) {
        await http.put(`/accounts/${editing.id}`, payload);
        toast.push("Account updated", "good");
      } else {
        await http.post("/accounts", payload);
        toast.push("Account created", "good");
      }
      setDialogOpen(false);
      reload();
    } catch (err) {
      toast.push(errMessage(err, "Could not save the account"), "critical");
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async () => {
    if (!toDelete) return;
    try {
      await http.del(`/accounts/${toDelete.id}`);
      toast.push("Account deleted", "good");
      setToDelete(null);
      reload();
    } catch (err) {
      toast.push(errMessage(err, "Could not delete the account"), "critical");
      throw err;
    }
  };

  const test = async (account: WhatsAppAccount) => {
    setTesting(account.id);
    try {
      // A credential failure is a 200 with success:false, so the result is
      // rendered on the card rather than thrown as a request error.
      const result = await http.post<AccountTestResult>(`/accounts/${account.id}/test`);
      setTests((prev) => ({ ...prev, [account.id]: result }));
      if (result.success) toast.push("Connection succeeded", "good");
      else toast.push(`Connection failed: ${result.error || "unknown error"}`, "critical");
    } catch (err) {
      const message = errMessage(err, "The connection test failed");
      setTests((prev) => ({ ...prev, [account.id]: { success: false, error: message } }));
      toast.push(message, "critical");
    } finally {
      setTesting(null);
    }
  };

  const subscribe = async (account: WhatsAppAccount) => {
    setSubscribing(account.id);
    try {
      const result = await http.post<AccountSubscribeResult>(`/accounts/${account.id}/subscribe`);
      if (result.success) toast.push(result.message || "App subscribed to webhooks", "good");
      else toast.push(`Subscribe failed: ${result.error || "unknown error"}`, "critical");
    } catch (err) {
      toast.push(errMessage(err, "Could not subscribe the app"), "critical");
    } finally {
      setSubscribing(null);
    }
  };

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>WhatsApp accounts</h1>
          <div className="sub">
            <a href="/settings">Settings</a> · the Meta credentials every message is sent with.
          </div>
        </div>
        <div className="page-actions">
          <button className="btn primary" onClick={openCreate} disabled={!canWrite}>
            + Add account
          </button>
        </div>
      </div>

      <ErrorBanner error={error} what="Could not load the accounts" />

      <Banner tone="info">
        <div className="strong">Webhook configuration</div>
        Point the Meta app&apos;s webhook at this URL and use the account&apos;s verify token.
        <div className="row" style={{ marginTop: 6 }}>
          <code className="mono">{webhookUrl}</code>
          <CopyButton value={webhookUrl} label="Copy webhook URL" />
        </div>
      </Banner>

      {loading ? (
        <div className="card">
          <div className="card-body">
            <SkeletonRows n={3} cols={3} />
          </div>
        </div>
      ) : accounts.length === 0 ? (
        <div className="card">
          <div className="card-body">
            <Empty
              title="No accounts connected"
              body="Connect a WhatsApp Business account before sending or receiving messages."
              icon="☎"
              action={
                canWrite ? (
                  <button className="btn primary sm" onClick={openCreate}>
                    + Add account
                  </button>
                ) : undefined
              }
            />
          </div>
        </div>
      ) : (
        <div className="stack">
          {accounts.map((account) => {
            const result = tests[account.id];
            return (
              <div className="card" key={account.id}>
                <div className="card-body">
                  <div className="row" style={{ alignItems: "flex-start" }}>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div className="chips">
                        <h3>{account.name}</h3>
                        <Badge tone={ACCOUNT_STATUS_TONE[account.status] ?? "warning"}>
                          {account.status || "unknown"}
                        </Badge>
                        {result?.is_test_number && <Badge tone="brand">Test number</Badge>}
                      </div>

                      {result && (
                        <div className="col" style={{ marginTop: 8 }}>
                          {result.success ? (
                            <div className="row">
                              <Badge tone="good">Connected</Badge>
                              {result.display_phone_number && (
                                <span className="muted">{result.display_phone_number}</span>
                              )}
                              {result.verified_name && (
                                <span className="muted">{result.verified_name}</span>
                              )}
                            </div>
                          ) : (
                            <div className="row">
                              <Badge tone="critical">Failed</Badge>
                              <span className="muted">{result.error}</span>
                            </div>
                          )}
                          {result.warning && <Banner tone="warning">{result.warning}</Banner>}
                        </div>
                      )}

                      <dl className="kv" style={{ marginTop: 12 }}>
                        {account.app_id && (
                          <>
                            <dt>App ID</dt>
                            <dd>
                              <code className="mono">{account.app_id}</code>
                            </dd>
                          </>
                        )}
                        <dt>Phone ID</dt>
                        <dd>
                          <span className="row">
                            <code className="mono">{account.phone_id}</code>
                            <CopyButton value={account.phone_id} label="Copy phone ID" />
                          </span>
                        </dd>
                        <dt>Business ID</dt>
                        <dd>
                          <code className="mono">{account.business_id}</code>
                        </dd>
                        <dt>API version</dt>
                        <dd>{account.api_version || "—"}</dd>
                        <dt>Access token</dt>
                        <dd>
                          <Badge tone={account.has_access_token ? "good" : "critical"}>
                            {account.has_access_token ? "Configured" : "Missing"}
                          </Badge>
                        </dd>
                        <dt>App secret</dt>
                        <dd>
                          <Badge tone={account.has_app_secret ? "good" : "warning"}>
                            {account.has_app_secret ? "Configured" : "Not set"}
                          </Badge>
                        </dd>
                        <dt>Verify token</dt>
                        <dd>
                          <span className="row">
                            <code className="mono truncate" style={{ maxWidth: 200 }}>
                              {account.webhook_verify_token}
                            </code>
                            <CopyButton value={account.webhook_verify_token} label="Copy verify token" />
                          </span>
                        </dd>
                      </dl>

                      <div className="chips" style={{ marginTop: 12 }}>
                        {account.is_default_incoming && <Tag label="Default incoming" />}
                        {account.is_default_outgoing && <Tag label="Default outgoing" />}
                        {account.auto_read_receipt && <Tag label="Auto read receipt" />}
                      </div>
                    </div>

                    <div className="row" style={{ flex: "none" }}>
                      <button
                        className="btn sm"
                        onClick={() => void test(account)}
                        disabled={testing === account.id}
                      >
                        {testing === account.id ? <Spinner label="Testing" /> : "⟳"} Test
                      </button>
                      <button
                        className="btn sm"
                        title="Subscribe this app to the account's webhooks"
                        onClick={() => void subscribe(account)}
                        disabled={subscribing === account.id || !canWrite}
                      >
                        {subscribing === account.id ? <Spinner label="Subscribing" /> : "◉"} Subscribe
                      </button>
                      <button
                        className="btn icon sm ghost"
                        title="Edit"
                        onClick={() => openEdit(account)}
                        disabled={!canWrite}
                      >
                        ✎
                      </button>
                      <button
                        className="btn icon sm ghost"
                        title="Business profile"
                        onClick={() => setProfileFor(account)}
                        disabled={!canWrite}
                      >
                        ⌂
                      </button>
                      <button
                        className="btn icon sm ghost"
                        title="Delete"
                        onClick={() => setToDelete(account)}
                        disabled={!canDelete}
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="card">
        <div className="card-head">
          <h3>Setup guide</h3>
        </div>
        <div className="card-body">
          <ol className="muted" style={{ fontSize: 13, paddingLeft: 20 }}>
            <li>
              Create a business app in the{" "}
              <a href="https://developers.facebook.com" target="_blank" rel="noreferrer">
                Meta developer console
              </a>{" "}
              and add the WhatsApp product to it.
            </li>
            <li>Add and verify the phone number you will send from.</li>
            <li>
              Copy the <strong>phone number ID</strong> and the{" "}
              <strong>WhatsApp business account ID</strong> from the API setup page.
            </li>
            <li>
              Create a system user with full access in{" "}
              <a
                href="https://business.facebook.com/settings/system-users"
                target="_blank"
                rel="noreferrer"
              >
                business settings
              </a>{" "}
              and generate a permanent token.
            </li>
            <li>Paste the credentials here and run the connection test.</li>
            <li>Set the webhook URL and verify token above, then press Subscribe.</li>
          </ol>
        </div>
      </div>

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title={editing ? "Edit account" : "Connect a WhatsApp account"}
        wide
      >
        <Field label="Account name" required>
          <input
            value={form.name}
            placeholder="Support line"
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
        </Field>

        <hr className="sep" />

        <Field label="Meta app ID" hint="Needed to upload template media.">
          <input value={form.app_id} onChange={(e) => setForm({ ...form, app_id: e.target.value })} />
        </Field>
        <Field label="Phone number ID" hint="From the WhatsApp API setup page." required>
          <input value={form.phone_id} onChange={(e) => setForm({ ...form, phone_id: e.target.value })} />
        </Field>
        <Field label="WhatsApp business account ID" required>
          <input
            value={form.business_id}
            onChange={(e) => setForm({ ...form, business_id: e.target.value })}
          />
        </Field>
        <Field
          label={editing ? "Access token (leave blank to keep the current one)" : "Access token"}
          hint="A permanent system-user token."
          required={!editing}
        >
          <input
            type="password"
            value={form.access_token}
            onChange={(e) => setForm({ ...form, access_token: e.target.value })}
          />
        </Field>
        <Field
          label={editing ? "App secret (leave blank to keep the current one)" : "App secret"}
          hint="Used to verify webhook signatures."
        >
          <input
            type="password"
            value={form.app_secret}
            onChange={(e) => setForm({ ...form, app_secret: e.target.value })}
          />
        </Field>

        <hr className="sep" />

        <Field label="Graph API version">
          <input
            value={form.api_version}
            placeholder="v21.0"
            onChange={(e) => setForm({ ...form, api_version: e.target.value })}
          />
        </Field>
        <Field label="Webhook verify token" hint="Left blank, one is generated for you.">
          <input
            value={form.webhook_verify_token}
            onChange={(e) => setForm({ ...form, webhook_verify_token: e.target.value })}
          />
        </Field>

        <hr className="sep" />

        <div className="section">
          <h4>Options</h4>
          <div className="setting">
            <div className="text">Default for incoming messages</div>
            <Toggle
              checked={form.is_default_incoming}
              onChange={(v) => setForm({ ...form, is_default_incoming: v })}
              ariaLabel="Default for incoming messages"
            />
          </div>
          <div className="setting">
            <div className="text">Default for outgoing messages</div>
            <Toggle
              checked={form.is_default_outgoing}
              onChange={(v) => setForm({ ...form, is_default_outgoing: v })}
              ariaLabel="Default for outgoing messages"
            />
          </div>
          <div className="setting">
            <div className="text">Send read receipts automatically</div>
            <Toggle
              checked={form.auto_read_receipt}
              onChange={(v) => setForm({ ...form, auto_read_receipt: v })}
              ariaLabel="Send read receipts automatically"
            />
          </div>
        </div>

        <DialogFooter>
          <button className="btn" onClick={() => setDialogOpen(false)} disabled={submitting}>
            Cancel
          </button>
          <button className="btn primary" onClick={() => void save()} disabled={submitting}>
            {submitting && <Spinner label="Saving" />}
            {editing ? "Update account" : "Create account"}
          </button>
        </DialogFooter>
      </Dialog>

      <BusinessProfileDialog
        account={profileFor}
        onClose={() => setProfileFor(null)}
      />

      <Confirm
        open={!!toDelete}
        onClose={() => setToDelete(null)}
        onConfirm={() => void remove()}
        title="Delete this account?"
        body={`"${toDelete?.name ?? ""}" will stop sending and receiving. Templates that reference it by name are left in place.`}
        danger
      />
    </div>
  );
}
